'use strict';
const _ = require('lodash');
const moment = require('moment');
const Utils = require('@nexios/utils').Utils;
const BigNumber = require('bignumber.js');
const isISODate = require('./helpers/isISODate');
const isUrl = require('./helpers/isUrl');
require('moment/locale/nl');
const isEmailAddress = require('./helpers/isEmailAddress');
BigNumber.config({ERRORS: false});
moment.locale('nl');

const baseTypes = {
  default: function (val) {
    return {
      value: val,
      valid: true
    };
  },
  string: function (val, errorMessage) {
    if (val === undefined || val === null) {
      return {
        value: val,
        valid: true
      };
    }

    if (!_.isString(val)) {
      return {
        value: val,
        valid: false,
        message: errorMessage || 'Dit is geen valide string!'
      };
    }

    if (val.length > 1000) {
      return {
        value: val,
        valid: false,
        message: errorMessage || 'De invoer is te lang. Maximaal 1000 tekens gebruiken.'
      };
    }

    return {
      value: val,
      valid: true
    };
  },
  memo: function (val, errorMessage) {
    if (val === undefined || val === null) {
      return {
        value: val,
        valid: true
      };
    }

    if (!_.isString(val)) {
      return {
        value: val,
        valid: false,
        message: errorMessage || 'Dit is geen valide string!'
      };
    }

    if (val.length > 5000) {
      return {
        value: val,
        valid: false,
        message: errorMessage || 'De invoer is te lang. Maximaal 5000 tekens gebruiken.'
      };
    }

    return {
      value: val,
      valid: true
    };
  },
  integer: function (val, errorMessage) {
    if (val === undefined || val === null) {
      return {
        value: val,
        valid: true
      };
    }

    // input should not start with leading zeros -> return invalid
    if (/^0[0-9].*$/.test(val)) {
      return {
        value: val,
        valid: false,
        message: errorMessage || 'Voorloopnullen zijn niet toegestaan.'
      };
    }

    // input should not contain a decimal separator
    if (!(/^[0-9]\d*$/.test(val))) {
      return {
        value: val,
        valid: false,
        message: errorMessage || 'Dit moet een geheel getal zijn.'
      };
    }

    var b = new BigNumber(val);
    var isInteger = b.isInteger(val);
    var msg = errorMessage || 'Dit moet een geheel getal zijn.';
    return {
      value: val,
      valid: isInteger,
      message: isInteger ? undefined : msg
    };
  },
  money: function (val, errorMessage) {
    if (val === undefined || val === null) {
      return {
        value: val,
        valid: true
      };
    }

    val = Utils.getNumberValue(val);

    if (_.isNil(val)) {
      return {
        value: val,
        valid: false,
        message: errorMessage || 'Dit is geen geldig bedrag.'
      };
    }

    var b = new BigNumber(val);
    var isFinite = b.isFinite(val);
    var msg = errorMessage || 'Dit is geen geldig bedrag.';
    return {
      value: val,
      valid: isFinite,
      message: isFinite ? undefined : msg
    };
  },
  money_strict: function (val, errorMessage) {
    if (_.isNil(val)) {
      return {
        value: val,
        valid: true
      };
    }

    if (!_.isString(val) || !/^-?[0-9]+(\.[0-9]{1,2})?$/.test(val)) {
      return {
        value: val,
        valid: false,
        message: errorMessage || 'Dit is geen geldig bedrag.'
      };
    } else {
      return {
        value: val,
        valid: true
      };
    }
  },
  date: function (val, errorMessage) {
    var msg = errorMessage || 'Dit is geen geldige datum (dd-mm-jjjj).';
    if (val === undefined || val === null) {
      return {
        value: val,
        valid: true
      };
    }

    if (_.isString(val)) {
      if (moment(val, ['YYYY-MM-DD', 'L', 'l', 'LL', 'll'], true).isValid()) {
        return {
          value: val,
          valid: true
        };
      }
    }

    if (moment.isMoment(val)) {
      return {
        value: val,
        valid: val.isValid()
      };
    }

    return {
      value: val,
      valid: false,
      message: msg
    };
  },
  date_iso: function (val, errorMessage) {
    var msg = errorMessage || 'Dit is geen geldige datum (jjjj-mm-dd).';
    if (val === undefined || val === null) {
      return {
        value: val,
        valid: true
      };
    }

    if (_.isString(val)) {
      if (isISODate(val)) {
        return {
          value: val,
          valid: true
        };
      }
    }

    if (moment.isMoment(val)) {
      return {
        value: val,
        valid: val.isValid()
      };
    }

    return {
      value: val,
      valid: false,
      message: msg
    };
  },
  combobox: function (val, errorMessage) {
    var msg = errorMessage || 'Dit is geen geldige waarde.';
    if (_.isString(val) && !_.isEmpty(val)) {
      return {
        value: val,
        valid: false,
        message: msg
      };
    } else {
      return {
        value: val,
        valid: true
      };
    }
  },
  bool: function (val, errorMessage) {
    if (!_.isBoolean(val)) {
      return {
        value: val,
        valid: false,
        message: errorMessage || 'Dit is geen geldige waarde.'
      };
    } else {
      return {
        value: val,
        valid: true
      };
    }
  },
  double: function (val, errorMessage) {
    if (val === undefined || val === null) {
      return {
        value: val,
        valid: true
      };
    }

    val = Utils.getNumberValue(val);

    if (_.isNil(val)) {
      return {
        value: val,
        valid: false,
        message: errorMessage || 'Dit is geen geldig getal.'
      };
    }

    var b = new BigNumber(val);
    var isFinite = b.isFinite(val);
    var msg = errorMessage || 'Dit is geen geldig getal.';
    return {
      value: val,
      valid: isFinite,
      message: isFinite ? undefined : msg
    };
  },
  website: function (val, errorMessage) {
    if (val === undefined || val === null || val === '') {
      return {
        value: val,
        valid: true
      };
    }
    if (isUrl(val)) {
      return {
        value: val,
        valid: true
      };
    } else {
      return {
        value: val,
        valid: false,
        message: errorMessage || 'Website is niet correct.'
      };
    }
  },
  email: function (val, errorMessage) {
    if (val === undefined || val === null || val === '') {
      return {
        value: val,
        valid: true
      };
    }
    if (isEmailAddress(val)) {
      return {
        value: val,
        valid: true
      };
    } else {
      return {
        value: val,
        valid: false,
        message: errorMessage || 'E-mailadres is niet correct.'
      };
    }
  },
  html: function (val) {
    if (_.isNil(val)) {
      return {
        value: val,
        valid: true
      };
    }

    const text = _.isFunction(val.getCurrentContent) ? val.getCurrentContent().getPlainText() : val;
    if (_.isString(text) && text.length > 15000) {
      return {
        value: val,
        valid: false,
        message: 'De invoer is te lang. Je mag maximaal 15.000 tekens gebruiken.'
      };
    }

    return {
      value: val,
      valid: true
    };
  }
};

baseTypes.datetime = function (val, errorMessage) {
  if (_.isNil(val) || _.isEmpty(val.date) || _.isEmpty(val.time)) {
    return {
      value: val,
      valid: true
    };
  }

  const dateValidation = baseTypes.date(val.date);

  if (!dateValidation.valid) {
    return dateValidation;
  }

  if (!/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/i.test(val.time)) {
    return {
      value: val,
      valid: false,
      message: errorMessage || 'Dit is geen geldige tijd.'
    };
  }

  return {
    value: val,
    valid: true
  };
};

baseTypes.time = function (val, errorMessage) {
  if (val === undefined || val === null) {
    return {
      value: val,
      valid: true
    };
  }

  if (!/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/i.test(val)) {
    return {
      value: val,
      valid: false,
      message: errorMessage || 'Dit is geen geldige tijd.'
    };
  }

  return {
    value: val,
    valid: true
  };
};

baseTypes.duration = function (val, errorMessage) {
  if (_.isNil(val) || _.isEmpty(val.hour) || _.isEmpty(val.minute)) {
    return {
      value: val,
      valid: true
    };
  }

  if (val.hour < 0 || !(/^[0-9]?[0-9]?[0-9]$/g.test(val.hour))) {
    return {
      value: val,
      valid: false,
      message: 'Alleen gehele getallen zijn toegestaan.'
    };
  }

  if (val.minute < 0 || !(/^[[0-9]?[0-9]$/g.test(val.minute))) {
    return {
      value: val,
      valid: false,
      message: 'Alleen gehele getallen zijn toegestaan.'
    };
  }

  if (val.minute > 59) {
    return {
      value: val,
      valid: false,
      message: 'Het aantal minuten mag niet groter zijn dan 59 minuten.'
    };
  }

  if (val.hour > 999) {
    return {
      value: val,
      valid: false,
      message: 'Het aantal uur mag niet groter zijn dan 999 uur.'
    };
  }

  return {
    value: val,
    valid: true
  };
};

baseTypes.postalCodeRange = function (val, errorMessage) {
  if (val === undefined || val === null) {
    return {
      value: val,
      valid: true
    };
  }

  if (/^((([1-9][0-9]{3}-[1-9][0-9]{3})|[1-9][0-9]{3})\s?,\s?)*(([1-9][0-9]{3}-[1-9][0-9]{3})|[1-9][0-9]{3})$/.test(val)) {
    return {
      value: val,
      valid: true
    };
  }

  return {
    value: val,
    valid: false,
    message: errorMessage || 'Dit is geen geldige postcode reeks.'
  };
};

module.exports = baseTypes;
